import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { WorkerSort } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  EmptyState,
  InlineRow,
  LoadingBlock,
  Muted,
  Select,
  Stack,
} from '../../components/ui';
import { useSearchWorkersQuery } from '../../services/serviceService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { WorkerCard, WorkersMap } from './ServiceGuestComponents';
import { ServiceSearchBar } from './ServiceSearchBar';
import {
  bookPath,
  parseServiceSearch,
  serviceSearchParams,
  toSearchInput,
  workerPath,
  type ServiceSearchState,
} from './serviceSearchState';

const Layout = styled.div`
  ${tw`grid grid-cols-1 gap-6 px-4 py-6 mx-auto lg:grid-cols-5 sm:px-6`}
  max-width: 1440px;
`;

const List = styled.div`
  ${tw`flex flex-col gap-4 lg:col-span-3 min-w-0`}
`;

const MapPane = styled.div`
  ${tw`lg:col-span-2 overflow-hidden`}
  height: 360px;
  border-radius: var(--radius-card, 12px);
  @media (min-width: 1024px) {
    position: sticky;
    top: 96px;
    height: calc(100vh - 128px);
  }
`;

const SORT_LABELS: Record<WorkerSort, string> = {
  [WorkerSort.Recommended]: 'Recommended',
  [WorkerSort.Distance]: 'Closest',
  [WorkerSort.Price]: 'Lowest price',
  [WorkerSort.Rating]: 'Best rated',
};

/**
 * Worker results (spec 019 W11): list and map, "Search this area", sort. Out
 * of range workers are not hidden: their card says why they cannot be booked
 * (D20). Every condition lives in the URL (`serviceSearchState.ts`).
 */
export default function ServiceSearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const state = useMemo(() => parseServiceSearch(params), [params]);
  const [page, setPage] = useState(1);
  const [hovered, setHovered] = useState<string | null>(null);
  const input = toSearchInput(state, page);
  const query = useSearchWorkersQuery({
    variables: { input: input! },
    skip: !input,
    fetchPolicy: 'cache-and-network',
  });
  const results = query.data?.searchWorkers;
  const items = results?.items ?? [];

  const update = (next: ServiceSearchState) => {
    setPage(1);
    setParams(serviceSearchParams(next));
  };

  const mapWorkers = items
    .filter(r => r.worker.areaCenter)
    .map(r => ({
      id: r.worker.carId,
      lat: r.worker.areaCenter!.lat,
      lng: r.worker.areaCenter!.lng,
      radiusMiles: r.worker.serviceRadiusMiles,
      distanceUnit: r.worker.distanceUnit,
    }));
  const center =
    state.lat !== undefined && state.lng !== undefined
      ? { lat: state.lat, lng: state.lng }
      : undefined;

  return (
    <Layout data-testid="service-search">
      <List>
        <ServiceSearchBar value={state} onSearch={update} compact />
        <InlineRow style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Muted $size="sm" data-testid="service-search-count">
            {results ? `${results.total} ${results.total === 1 ? 'worker' : 'workers'}` : ''}
          </Muted>
          <Select
            aria-label="Sort"
            value={state.sort}
            onChange={e => update({ ...state, sort: e.target.value as WorkerSort })}
            style={{ width: 200 }}
          >
            {Object.values(WorkerSort).map(sort => (
              <option key={sort} value={sort}>
                {SORT_LABELS[sort]}
              </option>
            ))}
          </Select>
        </InlineRow>
        {!input ? (
          <EmptyState
            icon={faMagnifyingGlass}
            title="Where is the job?"
            description="Enter a ZIP code or an address to see workers and prices."
          />
        ) : query.loading && !results ? (
          <LoadingBlock label="Finding workers" />
        ) : query.error && !results ? (
          <Alert tone="error" title="Search did not work">
            {stayErrorMessage(query.error)}
          </Alert>
        ) : items.length === 0 ? (
          <EmptyState
            icon={faMagnifyingGlass}
            title="No workers for this window"
            description="Try another time window, another day, or move the map."
          />
        ) : (
          <Stack $gap={4}>
            {items.map(result => (
              <WorkerCard
                key={result.worker.carId}
                result={result}
                onHover={hovering => setHovered(hovering ? result.worker.carId : null)}
                onOpen={() => navigate(workerPath(result.worker.carId, state))}
                onBook={() => navigate(bookPath(result.worker.carId, state))}
              />
            ))}
            {results && results.total > results.limit && (
              <InlineRow>
                <Button
                  type="button"
                  theme="secondary"
                  size="sm"
                  text="Previous"
                  isDisabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                />
                <Muted $size="sm">
                  Page {results.page} of {Math.ceil(results.total / results.limit)}
                </Muted>
                <Button
                  type="button"
                  theme="secondary"
                  size="sm"
                  text="Next"
                  isDisabled={results.page * results.limit >= results.total}
                  onClick={() => setPage(p => p + 1)}
                />
              </InlineRow>
            )}
          </Stack>
        )}
      </List>
      <MapPane>
        <WorkersMap
          workers={mapWorkers}
          center={center}
          hoveredId={hovered}
          onOpen={id => navigate(workerPath(id, state))}
          onSearchArea={bounds =>
            update({
              ...state,
              bounds,
              siteId: undefined,
              zip: undefined,
              lat: undefined,
              lng: undefined,
              where: 'Map area',
            })
          }
        />
      </MapPane>
    </Layout>
  );
}
